import { IconComponent, themeColor, themeFontFamily, UITypes } from 'LibNeo'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'

export type LinkProps = {
	hasAction?: boolean
	inText?: boolean
	disable?: boolean
	isSmall?: boolean
	type?: UITypes
}

const linkComponentCss = css`
	display: inline-flex;
	align-items: center;
	font-family: ${themeFontFamily('SBSansText')};
	line-height: 20px;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	text-decoration: underline;
	text-underline-offset: 4px;
	cursor: pointer;
	appearance: none;
	&:hover,
	&:focus {
		text-decoration: underline;
		text-underline-offset: 4px;
	}
	&:active {
		text-decoration: none;
	}
`
const types = {
	[UITypes.PRIMARY]: css`
		&,
		&:focus {
			color: ${themeColor('emerald', 5)};
			svg {
				fill: ${themeColor('emerald', 5)};
			}
		}
		&:focus {
			background-color: ${themeColor('emerald', 1)};
		}
		&:focus-visible {
			background-color: ${themeColor('emerald', 1)};
			color: ${themeColor('emerald', 5)};
			svg {
				fill: ${themeColor('emerald', 5)};
			}
		}
		&:hover,
		&:active {
			color: ${themeColor('emerald', 7)};
			svg {
				fill: ${themeColor('emerald', 7)};
			}
		}
	`,
	[UITypes.PASSIVE]: css`
		&,
		&:focus {
			color: ${themeColor('gray', 5)};
			svg {
				fill: ${themeColor('gray', 5)};
			}
		}
		&:focus {
			background-color: ${themeColor('gray', 2)};
		}
		&:focus-visible {
			background-color: ${themeColor('gray', 2)};
			color: ${themeColor('gray', 5)};
			svg {
				fill: ${themeColor('gray', 5)};
			}
		}
		&:hover,
		&:active {
			color: ${themeColor('gray', 6)};
			svg {
				fill: ${themeColor('gray', 6)};
			}
		}
	`,
	[UITypes.DANGER]: css`
		&,
		&:focus {
			color: ${themeColor('pink', 5)};
			svg {
				fill: ${themeColor('pink', 5)};
			}
		}
		&:focus {
			background-color: ${themeColor('pink', 1)};
		}
		&:focus-visible {
			background-color: ${themeColor('pink', 1)};
			color: ${themeColor('pink', 5)};
			svg {
				fill: ${themeColor('pink', 5)};
			}
		}
		&:hover,
		&:active {
			color: ${themeColor('pink', 7)};
			svg {
				fill: ${themeColor('pink', 7)};
			}
		}
	`,
	[UITypes.INFO]: css`
		&,
		&:focus {
			color: ${themeColor('blue', 5)};
			svg {
				fill: ${themeColor('blue', 5)};
			}
		}
		&:focus {
			background-color: ${themeColor('blue', 1)};
		}
		&:focus-visible {
			background-color: ${themeColor('blue', 1)};
			color: ${themeColor('blue', 5)};
			svg {
				fill: ${themeColor('blue', 5)};
			}
		}
		&:hover,
		&:active {
			color: ${themeColor('blue', 7)};
			svg {
				fill: ${themeColor('blue', 7)};
			}
		}
	`,
	[UITypes.WARNING]: css`
		&,
		&:focus {
			color: ${themeColor('gold', 6)};
			svg {
				fill: ${themeColor('gold', 6)};
			}
		}
		&:focus {
			background-color: ${themeColor('gold', 1)};
		}
		&:focus-visible {
			background-color: ${themeColor('gold', 1)};
			color: ${themeColor('gold', 5)};
			svg {
				fill: ${themeColor('gold', 5)};
			}
		}
		&:hover,
		&:active {
			color: ${themeColor('gold', 7)};
			svg {
				fill: ${themeColor('gold', 7)};
			}
		}
	`,
	[UITypes.BLACK]: css`
		&, 
		&:focus {
			color: ${themeColor('gray', 8)};
			svg {
				fill: ${themeColor('gray', 8)};
			}
		}
		&:focus {
			background-color: ${themeColor('gray', 1)};
		}
		&:focus-visible {
			background-color: ${themeColor('gray', 1)}
			color: ${themeColor('gray', 8)};
			svg {
				fill: ${themeColor('gray', 8)};
			}
		}
		&:hover,
		&:active {
			color: ${themeColor('gray', 9)};
			svg {
				fill: ${themeColor('gray', 9)};
			}
		}
	`,
}
const typeWithDisabled = {
	[UITypes.PRIMARY]: css`
		& {
			color: ${themeColor('emerald', 3)};
			svg {
				fill: ${themeColor('emerald', 3)};
			}
		}
	`,
	[UITypes.PASSIVE]: css`
		& {
			color: ${themeColor('gray', 4)};
			svg {
				fill: ${themeColor('gray', 4)};
			}
		}
	`,
	[UITypes.DANGER]: css`
		& {
			color: ${themeColor('pink', 3)};
			svg {
				fill: ${themeColor('pink', 3)};
			}
		}
	`,
	[UITypes.INFO]: css`
		& {
			color: ${themeColor('blue', 3)};
			svg {
				fill: ${themeColor('blue', 3)};
			}
		}
	`,
	[UITypes.WARNING]: css`
		& {
			color: ${themeColor('gold', 3)};
			svg {
				fill: ${themeColor('gold', 3)};
			}
		}
	`,
	[UITypes.BLACK]: css`
		& {
			color: ${themeColor('gray', 4)};
			svg {
				fill: ${themeColor('gray', 4)};
			}
		}
	`,
}
export const LinkStyled = styled(Link)<{
	type?: UITypes
}>`
	${linkComponentCss}
	${({ type }) => types[type]}
`
export const LinkIconComponentStyled = styled(IconComponent)`
	margin-left: 2px;
	transition: fill 0.3s;

	&,
	& > svg {
		height: 16px;
		width: 16px;
	}
`

export const LinkComponentStyled = styled.div<LinkProps>`
	${linkComponentCss}
	${({ type }) => types[type]}
	${({ type, disable }) => disable && typeWithDisabled[type]}	
	${({ inText }) =>
		inText &&
		css`
			display: inline;
			font-family: inherit;
			line-height: inherit;
			font-size: inherit;
			font-weight: inherit;
		`}
	${({ hasAction }) =>
		hasAction &&
		css`
			text-decoration: underline dotted;
			&:hover,
			&:focus {
				text-decoration: underline dotted;
			}
			&:active {
				text-decoration: none;
			}
		`}
	${({ disable }) =>
		disable &&
		css`
			// TODO переделать без этого правила, т.к. при таком состоянии не срабатывает событие на tooltip
			pointer-events: none;
		`}
	${({ isSmall }) =>
		isSmall &&
		css`
			font-size: 12px;
			${LinkIconComponentStyled}, ${LinkIconComponentStyled} > svg {
				height: 12px;
				width: 12px;
			}
		`}

    &:active {
		border: none;
	}

	&:focus {
		outline: none;
		box-shadow: none;
	}
`
export const LinkStyledWrapper = styled.div`
	${linkComponentCss}
`
